import { api } from './api';
import { toast } from 'sonner';
import { getQueue, removeFromQueue, getQueueCount, setCachedStations, getCachedStations } from './offline';

let syncing = false;
let listeners = new Set();

export function onSyncChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(state) {
  listeners.forEach(l => { try { l(state); } catch (e) { /* ignore */ } });
}

export async function syncQueue() {
  if (syncing) return { synced: 0, skipped: 0 };
  if (!navigator.onLine) return { synced: 0, skipped: 0 };
  syncing = true;
  let synced = 0, failed = 0;
  notify({ syncing: true });
  try {
    const items = await getQueue();
    for (const item of items) {
      try {
        const fd = new FormData();
        fd.append('station_id', item.stationId);
        fd.append('service_type', item.serviceType || 'self');
        fd.append('prices', JSON.stringify(item.prices || {}));
        fd.append('lat', String(item.lat ?? 0));
        fd.append('lng', String(item.lng ?? 0));
        fd.append('notes', item.notes || '');
        fd.append('no_gps', item.noGps ? 'true' : 'false');
        fd.append('queued', 'true');
        if (item.capturedAt) fd.append('captured_at_client', item.capturedAt);
        if (item.photoBlob) {
          const file = new File([item.photoBlob], item.photoName || 'photo.jpg', { type: item.photoType || 'image/jpeg' });
          fd.append('photo', file);
        }
        await api.post('/prices', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
        await removeFromQueue(item.id);
        synced++;
      } catch (e) {
        const code = e.response?.status;
        // If 401/403 we won't retry now
        if (code === 401 || code === 403) { failed++; break; }
        // Rechazo permanente del servidor (validación): sacar de la cola para
        // que no bloquee el resto, avisando al capturador el motivo.
        if (code >= 400 && code < 500) {
          await removeFromQueue(item.id);
          failed++;
          const detail = e.response?.data?.detail;
          toast.error(`Captura de ${item.stationName || 'estación'} rechazada`, {
            description: typeof detail === 'string' ? detail : 'Revisa los datos y captura de nuevo.',
            duration: 10000,
          });
          continue;
        }
        // Error de red / servidor: se reintenta en la próxima sincronización
        failed++;
      }
    }
  } finally {
    syncing = false;
    const remaining = await getQueueCount();
    notify({ syncing: false, synced, failed, remaining });
  }
  return { synced, failed };
}

export async function refreshStationsCache() {
  try {
    const r = await api.get('/stations');
    await setCachedStations(r.data);
    return r.data;
  } catch (e) {
    return await getCachedStations();
  }
}

/* Auto-sync when connection returns */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { syncQueue(); refreshStationsCache(); });
}
